import {ImageApi} from "../image/ImageApi"
import {action, computed, makeObservable, observable, runInAction} from "mobx"
import {Category, Contest, Submission, WorkType} from "./Submission";
import {SubmissionItem} from "./SubmissionItem";
import {MobxSubmissionItem} from "./MobxSubmissionItem";
import {UploadInProgress} from "./UploadInProgress";
import {option} from "ts-option/index";
import {ApolloClient, gql} from "@apollo/client";
import exifr from 'exifr'
import * as _ from "lodash";

export class MobxSubmission implements Submission {
  client: ApolloClient<any>
  user: string
  userName: string
  submissionId: string

  uploadsInProgress: UploadInProgress[] = []
  uploads: Array<MobxSubmissionItem> = []
  removed: MobxSubmissionItem[] = []
  submitted: boolean = false
  createdAt: string
  updatedAt: string
  category: Category
  workType: WorkType
  contest: Contest

  constructor(client: ApolloClient<any>, user: string, userName: string, contest: Contest, category: Category, workType: WorkType, items: Array<MobxSubmissionItem>, s?: string, submitted?: boolean, createdAt?: string, updatedAt?: string) {
    this.client = client
    this.user = user
    this.submissionId = s
    this.contest = contest
    this.submitted = submitted
    this.createdAt = createdAt
    this.updatedAt = updatedAt
    this.userName = userName

    makeObservable(this, {
        addNew: action,
        move: action,
        remove: action,
        removed: observable,
        items: computed,
        uploadsInProgress: observable,
        uploads: observable,
        newUploads: computed,
        dirtyItems: computed,
        dirty: computed,
        setSubmissionId: action,
        permanentlyRemoved: action,
        submitted: observable,
        submissionId: observable,
        addUploaded: action,
        setUploads: action,
        updatedNow: action,
        setRemoved: action
      }
    )

    this.uploads = Array.from(items.map(x => {
      x.setSaved();
      return x
    })).sort((x, y) => x.sortKey < y.sortKey ? -1 : 1)
    this.category = category
    this.workType = workType
  }

  addUploaded(item: MobxSubmissionItem) {
    this.uploads = [item, ...this.uploads]
    this.uploadsInProgress = this.uploadsInProgress.filter(i => i.id !== item.id)
  }

  move(from: number, to: number) {
    if (from !== to) {
      runInAction(() => {

      })
      const [mover] = this.uploads.splice(from, 1)

      mover.sortKey = sortKeyBefore(this.uploads, to)
      mover.setDirty()

      this.uploads.splice(to, 0, mover)

      this.setUploads(this.uploads)
    }
  }

  setRemoved(r: MobxSubmissionItem[]) {
    this.removed = r
  }

  remove(item: SubmissionItem) {
    this.uploads = this.uploads.filter(i => i.id !== item.id)
    if (item.saved)
      this.removed.unshift(item)
  }

  permanentlyRemoved(id: string) {
    this.removed = this.removed.filter(i => i.id !== id)
  }

  addNew(file: File) {
    const upload = new UploadInProgress(file)

    let progress = p => {
      upload.update(p);
    }

    this.uploadsInProgress.push(upload)

    Promise.all(
      [
        ImageApi.upload(file, file.type, this.user, progress),
        exifr.parse(file, {xmp:true}).catch(() => {})
      ])
      .then(([image, metadata]) => {
        return new MobxSubmissionItem(upload.id, file.name, image.url, image.contentType, image.key, sortKeyBefore(this.uploads, 0), metadata || {});
      })
      .then(item => this.addUploaded(item))
  }

  setUploads(items: MobxSubmissionItem[]) {
    this.uploads = items
  }

  setSubmitted() {
    this.submitted = true
  }

  get items(): SubmissionItem[] {
    return this.uploads;
  }

  get newUploads(): Array<MobxSubmissionItem & { sortKey: Number }> {
    return this.uploads.filter(item => !item.saved)
  }

  get dirtyItems(): Array<MobxSubmissionItem> {
    return this.uploads.filter(item => item.dirty)
  }

  get dirty(): boolean {
    return this.newUploads.length > 0 || this.dirtyItems.length > 0 || this.removed.length > 0
  }

  setSubmissionId(submissionId: string) {
    this.submissionId = submissionId
    return submissionId
  }

  createAsNew(): Promise<string> {


    let input = {
      owner: this.userName,
      contestId: this.contest.id,
      categoryId: this.category.id,
      workTypeId: this.workType.id,
      saved: true,
    };

    return this.client.mutate({
      mutation: createSubmissionMutation,
      variables: {
        input: input
      }
    }).then(({data: {createSubmission: {id}}}) => {
      this.submissionId = id
      return id
    })
  }

  saveNewItems(submissionId: string): Array<Promise<string>> {
    return this.newUploads.map(item =>
      this.client.mutate({
        mutation: createSubmissionItemMutation,
        variables: {
          input: {
            id: item.id,
            owner: this.userName,
            sortKey: item.sortKey,
            url: item.url,
            contentType: item.contentType,
            fileName: item.fileName,
            submissionId: submissionId,
            metadata: JSON.stringify(item.metadata)
          }
        }
      }).then(() => {
        item.setSaved()
        return item.id
      })
    );
  }

  updatedNow() {
    this.updatedAt = new Date().toISOString()
  }

  updateUpdatedItems() {
    return this
      .dirtyItems.map(item => {
        return this.client.mutate({
            mutation: updateSubmissionItemMutation, variables: {
              input: {
                id: item.id,
                sortKey: item.sortKey,
                metadata: JSON.stringify(item.metadata)
              }
            }
          }).then(() => {
            item.setSaved()
            return item.id
          });
        }
      )
  }

  removeRemovedItems(submissionId: string): Promise<String>[] {
    if (!submissionId)
      return []

    return this.removed.map(item => {
      return this.client.mutate(
        {mutation: deleteSubmissionItemMutation, variables: {input: {id: item.id}}}
      ).then(() => {
        this.setUploads(this.uploads.filter(upload => upload.id !== item.id))
        this.setRemoved(this.removed.filter(removed => removed.id !== item.id))
        return item.id
      }).catch((e) => {

        return ""
      })
    })
  }

  save(): Promise<string> {

    this.dirtyItems.forEach(item => item.preSave())

    return option(this.submissionId)
      .map(id => Promise.resolve(id))
      .getOrElse(() => this.createAsNew())
      .then(id => {
        Promise.all([
          ...this.saveNewItems(id),
          ...this.updateUpdatedItems(),
          ...this.removeRemovedItems(id)])
          .then(() => id)
        this.updatedNow()
        return id
      })
  }
}

const createSubmissionItemMutation = gql`
    mutation($input: CreateSubmissionItemInput!) {
        createSubmissionItem(input: $input) {
            id
        }
    }
`

const deleteSubmissionItemMutation = gql`
    mutation($input: DeleteSubmissionItemInput!) {
        deleteSubmissionItem(input: $input) {
            id
        }
    }
`

const updateSubmissionItemMutation = gql`
    mutation($input: UpdateSubmissionItemInput!) {
        updateSubmissionItem(input: $input) {
            id
        }
    }
`

const createSubmissionMutation = gql`
    mutation($input: CreateSubmissionInput!) {
        createSubmission(input: $input) {
            id
        }
    }
`

/**
 * DynamoDb requires a sort key for sorting.
 *
 * This Generates the sort key for insertion at index
 *
 * No existing items                => 0
 * Insert at beginning              => sortKey of first item - 1
 * Insert at end                    => sortKey of last item + 1
 * Inert between existing items     => half way between sortKeys of the surrounding items
 *
 */
function sortKeyBefore(existing: Array<MobxSubmissionItem>, index: number): number {
  if (existing.length === 0) {
    return 0
  }

  if (index === 0) {
    return existing[0].sortKey - 1
  } else if (index === existing.length - 1) {
    return existing[existing.length - 1].sortKey + 1
  }

  if (existing[index - 1] && existing[index]) {
    return (existing[index - 1].sortKey + existing[index].sortKey) / 2.0
  } else {
    return existing[existing.length - 1].sortKey + 1
  }
}
